import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  input,
  output,
} from '@angular/core';
import { MAT_DATE_RANGE_SELECTION_STRATEGY } from '@angular/material/datepicker';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { QarepaProgressBarService } from '@qarepa/common';
import { WeekRangeSelectionStrategyService } from '@qaroni-core/services/app/date/week-range-selection-strategy.service';
import { DayISO } from '@qaroni-core/types/date/days.enum';
import { RangeWeek } from '@qaroni-core/types/date/range-week';
import { WeekRangeConvert } from '@qaroni-core/utils/week-range-convert';
import { WeekDataPickerData } from '@qaroni-shared/types/week-data-picker-data';
import {
  addDays,
  addWeeks,
  format,
  getISODay,
  getWeek,
  isValid,
  parseISO,
  startOfYear,
  subYears,
} from 'date-fns';
import { WeekDatepickerForm } from './week-datepicker.form';

@Component({
  selector: 'qaroni-week-datepicker',
  templateUrl: './week-datepicker.component.html',
  styleUrl: './week-datepicker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: WeekRangeSelectionStrategyService,
    },
  ],
  standalone: false,
})
export class WeekDatepickerComponent
  extends WeekDatepickerForm
  implements OnInit
{
  readonly showWeekNumber = input<boolean>(true);
  readonly useQueryParam = input<boolean>(true);
  readonly weekNumberParamName = input<string>('weekNumber');
  readonly yearParamName = input<string>('year');
  readonly startDateParamName = input<string>('startDate');
  readonly endDateParamName = input<string>('endDate');
  readonly borderLess = input<boolean>(true);
  readonly mode = input<'week' | 'rangeDate'>('week');

  readonly date = output<WeekDataPickerData>();
  readonly currentWeek = output<RangeWeek>();

  private route = inject(ActivatedRoute);
  private progressBar = inject(QarepaProgressBarService);

  public weekNumber = 0;
  public minDate = subYears(new Date(), 100);
  private currentDate: Date = new Date();

  public isLoading$ = this.progressBar.getProgressBar$();
  private queryParamMap$ = this.route.queryParamMap;

  ngOnInit(): void {
    if (this.useQueryParam()) {
      this.subs.add(this.queryParamMap$.subscribe(this.getQueryParamMap));
    } else {
      this.getCurrentWeek();
    }
  }

  get currentYear(): string {
    return this.endDate.value ? format(this.endDate.value, 'yyyy') : '';
  }

  get ngConditionalsClasses() {
    return {
      invisible: !this.weekNumber,
      border: !this.borderLess(),
    };
  }

  private getCurrentWeek(): void {
    this.currentDate = new Date();
    this.setCurrentWeekDates();
    this.weekNumber = this.getWeekNumber(this.startDate.value);

    this.currentWeek.emit({
      startDate: this.startDate.value,
      endDate: this.endDate.value,
    });
  }

  public onClosedDateRange() {
    this.currentDate = new Date(this.startDate.value);
    this.weekNumber = this.getWeekNumber(this.startDate.value);

    this.currentWeek.emit({
      startDate: this.startDate.value,
      endDate: this.endDate.value,
    });

    const weekData: WeekDataPickerData = {
      weekNumber: this.weekNumber,
      year: parseInt(format(this.endDate.value, 'yyyy')),
      currentDate: this.currentDate,
      startDate: this.startDate.value,
      endDate: this.endDate.value,
    };

    this.date.emit(weekData);
  }

  public addWeek() {
    this.startDate.setValue(addWeeks(this.startDate.value, 1));
    this.endDate.setValue(addWeeks(this.endDate.value, 1));
    this.weekNumber = this.getWeekNumber(this.startDate.value);
    this.currentDate = new Date(this.startDate.value);

    this.currentWeek.emit({
      startDate: this.startDate.value,
      endDate: this.endDate.value,
    });

    const weekData: WeekDataPickerData = {
      weekNumber: this.weekNumber,
      year: parseInt(format(this.endDate.value, 'yyyy')),
      currentDate: this.startDate.value,
      startDate: this.startDate.value,
      endDate: this.endDate.value,
    };

    this.date.emit(weekData);
  }

  public subtractWeek() {
    this.startDate.setValue(addWeeks(this.startDate.value, -1));
    this.endDate.setValue(addWeeks(this.endDate.value, -1));

    this.weekNumber = this.getWeekNumber(this.startDate.value);
    this.currentDate = new Date(this.startDate.value);

    this.currentWeek.emit({
      startDate: this.startDate.value,
      endDate: this.endDate.value,
    });

    const weekData: WeekDataPickerData = {
      weekNumber: this.weekNumber,
      year: parseInt(format(this.endDate.value, 'yyyy')),
      currentDate: this.startDate.value,
      startDate: this.startDate.value,
      endDate: this.endDate.value,
    };

    this.date.emit(weekData);
  }

  private getWeekNumber(date: Date): number {
    return isValid(date)
      ? getWeek(date, {
          weekStartsOn: 1,
          firstWeekContainsDate: getISODay(startOfYear(new Date())) as DayISO,
        })
      : getWeek(new Date(), {
          weekStartsOn: 1,
          firstWeekContainsDate: getISODay(startOfYear(new Date())) as DayISO,
        });
  }

  private getQueryParamMap = (queryParamMap: ParamMap): void => {
    if (this.mode() === 'rangeDate') {
      const startDateParamName = this.startDateParamName();
      const endDateParamName = this.endDateParamName();
      if (
        queryParamMap.has(startDateParamName) &&
        queryParamMap.has(endDateParamName)
      ) {
        const startDate = queryParamMap.get(startDateParamName);
        const endDate = queryParamMap.get(endDateParamName);

        this.startDate.setValue(parseISO(startDate), 'yyyy-MM-dd');
        this.endDate.setValue(parseISO(endDate), 'yyyy-MM-dd');
        this.weekNumber = this.getWeekNumber(parseISO(startDate));
        this.currentDate = parseISO(startDate);
      }
      return;
    }

    const weekNumberParamName = this.weekNumberParamName();
    const yearParamName = this.yearParamName();
    if (
      queryParamMap.has(weekNumberParamName) &&
      queryParamMap.has(yearParamName)
    ) {
      const weekNumber: number = parseInt(
        queryParamMap.get(weekNumberParamName)
      );
      const year: number = parseInt(queryParamMap.get(yearParamName));

      if (Number.isInteger(weekNumber) && year) {
        this.weekNumber = weekNumber;

        const sunday = new Date(year, 0, 2 + weekNumber * 7);
        while (sunday.getDay() !== 0) {
          sunday.setDate(sunday.getDate() - 1);
        }

        this.currentDate = new Date(sunday);
        this.setCurrentWeekDates();

        this.currentWeek.emit({
          startDate: this.startDate.value,
          endDate: this.endDate.value,
        });
      }
    }
  };

  private setCurrentWeekDates(): void {
    const subtractDays = WeekRangeConvert.convert(this.currentDate)[0];
    const plusDays = WeekRangeConvert.convert(this.currentDate)[1];

    this.startDate.setValue(addDays(this.currentDate, subtractDays));
    this.endDate.setValue(addDays(this.currentDate, plusDays));
  }
}
